import { Card, Button, Skeleton, useSpinner } from '@chikara/ui';
import { formatCurrency } from '@chikara/utils';
import { t } from '@/i18n';
import type { CreditSummary } from '../../types/credits.types';
import styles from './CreditSummarySection.module.css';

export interface CreditSummarySectionProps {
  data: CreditSummary;
  onMakePayment?: () => void;
}

export function CreditSummarySection({ data, onMakePayment }: CreditSummarySectionProps) {
  const { isSpinnerLoading } = useSpinner();
  const formatMXN = (value: number) =>
    formatCurrency(value, { locale: 'es-MX', currency: 'MXN' });

  return (
    <section className={styles.section} aria-labelledby="credit-summary-title">
      <h2 id="credit-summary-title" className={styles.sectionTitle}>
        {t('credits.summaryTitle')}
      </h2>
      <div className={styles.wrapper}>
        {isSpinnerLoading ? (
          <>
            <Card className={styles.creditLineCard}>
              <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <Skeleton width="100px" height="14px" />
                <Skeleton width="180px" height="28px" />
              </div>
            </Card>
            <Card className={styles.detailsCard}>
              <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between', padding: '1rem', gap: '1rem' }}>
                 <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <Skeleton width="80px" height="14px" />
                    <Skeleton width="120px" height="24px" />
                 </div>
                 <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <Skeleton width="80px" height="14px" />
                    <Skeleton width="120px" height="24px" />
                 </div>
                 <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <Skeleton width="80px" height="14px" />
                    <Skeleton width="120px" height="24px" />
                 </div>
                 <Skeleton width="140px" height="48px" style={{ borderRadius: '8px' }} />
              </div>
            </Card>
          </>
        ) : (
          <>
            <Card className={styles.creditLineCard}>
              <Card.Stats
                label={t('credits.creditLineLabel')}
                value={formatMXN(data.creditLine)}
              />
            </Card>
            <Card className={styles.detailsCard}>
              <Card.Stats
                label={t('credits.spentLabel')}
                value={formatMXN(data.spent)}
                className={styles.statItem}
              />
              <Card.Stats
                label={t('credits.availableLabel')}
                value={formatMXN(data.available)}
                className={styles.statItem}
              />
              <Card.Stats
                label={
                  <span className={styles.toPayLabel}>
                    <span>{t('credits.toPayLabel')}</span>
                    <span className={styles.toPayDate}>{data.toPayDate}</span>
                  </span>
                }
                value={formatMXN(data.toPayAmount)}
                className={styles.statItem}
              />
              <Button
                type="button"
                variant="primary"
                size="lg"
                className={styles.payButton}
                onClick={onMakePayment}
                aria-label={t('credits.makePayment')}
              >
                {t('credits.makePayment')}
              </Button>
            </Card>
          </>
        )}
      </div>
    </section>
  );
}

CreditSummarySection.displayName = 'CreditSummarySection';
